/**
 * ShiftCard Component
 * Extracted from Schedule.jsx - Detailed shift card with expandable inline panel
 */
import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { fmt } from '../../lib/currency';
import { 
  User,
  MapPin,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ShieldAlert,
  Briefcase,
  ChevronDown,
  ChevronUp,
  PoundSterling
} from 'lucide-react';

// Shift type color definitions
const SHIFT_COLORS = {
  regular: { bg: 'bg-[#1ABC9C]/20', border: 'border-[#1ABC9C]', text: 'text-[#1ABC9C]', dot: 'bg-[#1ABC9C]' },
  night: { bg: 'bg-indigo-500/20', border: 'border-indigo-500', text: 'text-indigo-400', dot: 'bg-indigo-500' },
  standby: { bg: 'bg-[#FFC04D]/20', border: 'border-[#FFC04D]', text: 'text-[#FFC04D]', dot: 'bg-[#FFC04D]' },
  emergency: { bg: 'bg-red-500/20', border: 'border-red-500', text: 'text-red-400', dot: 'bg-red-500' },
};

const STATUS_COLORS = {
  pending: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  confirmed: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  standby: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
  completed: 'bg-gray-500/15 text-gray-400 border-gray-500/20',
  cancelled: 'bg-red-500/15 text-red-400 border-red-500/20',
  no_show: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
};

const COMPLIANCE_COLORS = {
  compliant: 'text-[#1ABC9C]',
  expiring_soon: 'text-[#FFC04D]',
  expired: 'text-red-400',
  pending: 'text-gray-400',
};

/**
 * ShiftCard - Detailed shift card with expandable inline panel
 * @param {Object} props
 * @param {Object} props.shift - Shift data
 * @param {Array} props.medics - List of medics for assignment dropdown
 * @param {string|null} props.expandedShiftId - ID of currently expanded shift
 * @param {Object|null} props.draggedShift - Currently dragged shift
 * @param {Function} props.getShiftConflicts - Function to check for conflicts
 * @param {Function} props.toggleShiftExpand - Handler for toggling expansion
 * @param {Function} props.closeExpandedShift - Handler for closing expanded shift
 * @param {Function} props.handleDragStart - Handler for drag start
 * @param {Function} props.handleUpdateShift - Handler for updating shift
 * @param {Function} props.handleMedicAssignment - Handler for medic assignment
 * @param {Function} props.handleDeleteShift - Handler for deleting shift
 */
export function ShiftCard({ 
  shift, 
  medics,
  expandedShiftId,
  draggedShift,
  getShiftConflicts,
  toggleShiftExpand,
  closeExpandedShift,
  handleDragStart,
  handleUpdateShift,
  handleMedicAssignment,
  handleDeleteShift,
  handleMarkNoShow
}) {
  const colors = SHIFT_COLORS[shift.shift_type] || SHIFT_COLORS.regular;
  const conflicts = getShiftConflicts(shift);
  const hasConflict = conflicts.length > 0;
  const medic = medics.find(m => m.id === shift.medic_id);
  const isExpanded = expandedShiftId === shift.id;

  // Multi-day segment info set by getShiftsForDay in Schedule.jsx. If the
  // card is rendering on a middle day we show a dashed border + muted
  // "Day X of Y" label so coordinators see the whole run visually.
  const segment = shift._multiDaySegment || (shift.is_multi_day ? 'start' : null);
  const isMultiDaySegment = !!segment;
  const isMiddleSegment = segment === 'middle';
  const isEndSegment = segment === 'end';
  const multiDayAccent = isMultiDaySegment ? 'border-l-indigo-400 border-l-4' : '';

  return (
    <div
      className={`
        block w-full min-w-0 rounded-lg overflow-hidden transition-all duration-200
        ${colors.bg} ${isMultiDaySegment ? multiDayAccent : `border-l-4 ${colors.border}`}
        ${isMiddleSegment ? 'bg-indigo-500/10 ring-1 ring-indigo-400/20' : ''}
        ${hasConflict ? 'ring-2 ring-red-500/50' : ''}
        ${draggedShift?.id === shift.id ? 'opacity-50' : ''}
      `}
      data-testid={`shift-card-${shift.id}`}
      title={isMultiDaySegment ? `Multi-day assignment: ${shift.date} → ${shift.end_date} (Day ${shift._dayIndex || 1} of ${shift._dayTotal || 1})` : undefined}
    >
      {/* Main Shift Info - Always Visible */}
      <div
        draggable
        onDragStart={(e) => handleDragStart(e, shift)}
        onClick={(e) => toggleShiftExpand(shift.id, e)}
        className="p-3.5 cursor-pointer hover:bg-white/5 transition-colors"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {/* Shift Reference — prominent, operational anchor */}
            {shift.reference && (
              <div className="flex items-center gap-1.5 mb-1.5">
                <span
                  className="text-[11px] font-semibold tracking-wider text-[#A1A1AA] font-['JetBrains_Mono']"
                  data-testid={`shift-ref-${shift.id}`}
                >
                  {shift.reference}
                </span>
                {shift.shift_type && shift.shift_type !== 'regular' && (
                  <span className={`text-[10px] uppercase tracking-wider font-semibold ${colors.text}`}>
                    · {shift.shift_type}
                  </span>
                )}
              </div>
            )}

            {/* Time */}
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className={`text-sm font-semibold ${colors.text}`}>
                {isMiddleSegment ? 'All day' : (isEndSegment ? `ends ${shift.end_time}` : shift.start_time)}
                {!isMultiDaySegment && ` - ${shift.end_time}`}
                {segment === 'start' && ` →`}
              </span>
              {isMultiDaySegment && (
                <Badge
                  className="bg-indigo-500/25 text-indigo-200 border border-indigo-400/40 text-xs font-semibold"
                  data-testid={`shift-multi-day-badge-${shift.id}`}
                  title={`Continuous assignment ${shift.date} → ${shift.end_date}`}
                >
                  {segment === 'start' && `▶ Starts · Day 1 of ${shift._dayTotal || 1}`}
                  {segment === 'middle' && `■ Day ${shift._dayIndex} of ${shift._dayTotal}`}
                  {segment === 'end' && `◀ Ends · Day ${shift._dayTotal || 1}`}
                </Badge>
              )}
            </div>

            {/* Job Type — operationally more useful than the venue name on the card */}
            {shift.job_type_name && (
              <div className="flex items-center gap-1.5 text-sm text-[#F2F2F2] mb-1.5">
                <Briefcase className="w-3 h-3 flex-shrink-0 text-[#A1A1AA]" />
                <span className="truncate" data-testid={`shift-job-type-${shift.id}`}>{shift.job_type_name}</span>
              </div>
            )}

            {/* Medic — full name, never just the first */}
            <div className="flex items-center gap-1.5 text-sm">
              <User className="w-3 h-3 flex-shrink-0 text-[#A1A1AA]" />
              {shift.medic_id ? (
                <span className="text-[#F2F2F2] truncate flex items-center gap-1" data-testid={`shift-medic-${shift.id}`}>
                  {shift.medic_name}
                  {medic && (
                    <span className={COMPLIANCE_COLORS[medic.compliance_status]}>
                      {medic.compliance_status === 'compliant' && <CheckCircle className="w-3 h-3" />}
                      {medic.compliance_status === 'expiring_soon' && <AlertTriangle className="w-3 h-3" />}
                      {medic.compliance_status === 'expired' && <XCircle className="w-3 h-3" />}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-[#FFC04D] italic">Unassigned</span>
              )}
            </div>
          </div>

          {/* Right side indicators — status gets its own column with
              generous padding so long labels (confirmed, cancelled) are
              never clipped. */}
          <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
            <Badge
              className={`${STATUS_COLORS[shift.status]} text-[11px] font-medium px-2.5 py-0.5 capitalize whitespace-nowrap`}
              data-testid={`shift-status-${shift.id}`}
            >
              {(shift.status || '').replace('_', ' ')}
            </Badge>
            {hasConflict && (
              <ShieldAlert className="w-4 h-4 text-red-400" />
            )}
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-[#A1A1AA]" />
            ) : (
              <ChevronDown className="w-4 h-4 text-[#A1A1AA]" />
            )}
          </div>
        </div>
      </div>

      {/* Expandable Details Panel */}
      {isExpanded && (
        <div 
          className="border-t border-white/10 bg-[#1A1A1A]/50 p-3 space-y-3"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                closeExpandedShift?.();
              }}
              className="h-6 w-6 p-0 text-[#A1A1AA] hover:text-[#F2F2F2]"
              data-testid={`shift-card-close-${shift.id}`}
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
          </div>

          {/* Conflict Warning */}
          {hasConflict && (
            <div className="p-2 bg-red-500/10 border border-red-500/20 rounded text-xs">
              <div className="flex items-center gap-1 text-red-400 font-medium">
                <ShieldAlert className="w-3 h-3" />
                Conflict: {conflicts.length} overlapping shift(s)
              </div>
            </div>
          )}

          {/* Additional Details */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            {shift.job_type_name && (
              <div className="flex items-center gap-1 text-[#A1A1AA]">
                <Briefcase className="w-3 h-3" />
                <span>{shift.job_type_name}</span>
              </div>
            )}
            <div className="flex items-center gap-1 text-[#1ABC9C]">
              <PoundSterling className="w-3 h-3" />
              <span>{fmt(shift.rate_payable, shift.currency || 'GBP', { decimals: 0 })}</span>
            </div>
          </div>

          {/* Assign/Reassign Medic */}
          <div className="space-y-1">
            <Label className="text-xs text-[#A1A1AA]">Assign Medic</Label>
            <Select 
              value={shift.medic_id || 'none'} 
              onValueChange={(value) => {
                if (value === 'none') {
                  handleUpdateShift(shift.id, { medic_id: null });
                } else {
                  handleMedicAssignment(value, shift);
                }
              }}
            >
              <SelectTrigger className="h-8 bg-[#2B2B2B] border-white/10 text-xs">
                <SelectValue placeholder="Select medic" />
              </SelectTrigger>
              <SelectContent className="bg-[#2B2B2B] border-white/10">
                <SelectItem value="none">Unassigned</SelectItem>
                {medics.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    <span className={COMPLIANCE_COLORS[m.compliance_status]}>
                      {m.compliance_status === 'expired' ? '⚠️ ' : m.compliance_status === 'expiring_soon' ? '⏰ ' : '✓ '}
                    </span>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Update Status */}
          <div className="space-y-1">
            <Label className="text-xs text-[#A1A1AA]">Status</Label>
            <Select 
              value={shift.status} 
              onValueChange={(value) => handleUpdateShift(shift.id, { status: value })}
            >
              <SelectTrigger className="h-8 bg-[#2B2B2B] border-white/10 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#2B2B2B] border-white/10">
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="standby">Standby</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="no_show">No-show</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          {shift.notes && (
            <div className="p-2 bg-[#2B2B2B] rounded text-xs text-[#A1A1AA]">
              {shift.notes}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            {shift.medic_id && shift.status !== 'no_show' && shift.status !== 'cancelled' && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkNoShow?.(shift.id);
                }}
                className="flex-1 h-7 text-xs border-orange-500/40 text-orange-400 hover:bg-orange-500/10"
                data-testid={`mark-no-show-btn-${shift.id}`}
              >
                <AlertTriangle className="w-3 h-3 mr-1" />
                Mark no-show
              </Button>
            )}
            <Button
              variant="destructive"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteShift(shift.id);
              }}
              className="flex-1 h-7 text-xs"
              data-testid={`delete-shift-btn-${shift.id}`}
            >
              Delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export { SHIFT_COLORS, STATUS_COLORS, COMPLIANCE_COLORS };
